import qs from 'qs';
import type {
    GetKycPendingActionsApiResponse,
    QuestionnaireRequest,
    GetKycQuestionnairePageDefinitionApiResponse,
    GetMemberVerificationStatusApiResponse,
    InitializeQuestionnaireApiRequest,
    InitializeQuestionnaireApiResponse,
    CompleteQuestionnaireApiResponse,
    CompleteQuestionnaireApiRequest,
    GetMemberCompanyMoneyTransferLicenseStatusApiResponse,
    SetMakeAnOfferMemberEnabledApiResponse,
    SetMakeAnOfferMemberEnabledApiRequest,
    SetMessagingSilentApiRequest,
    SetMessagingSilentApiResponse,
    GetMessagingSilentApiResponse,
    GetMemberEmailNotificationsSettingsViewModelApiResponse,
    SetMessagingEmailNotifyApiResponse,
    SetMessagingEmailNotifyApiRequest,
    UpdateMemberEmailSettingsApiResponse,
    UpdateMemberEmailSettingsApiRequest,
    GetMemberCompanyDac7StatusApiResponse,
    UpdateMemberVatNumberApiRequest,
    UpdateMemberVatNumberApiResponse,
    GetMemberHasTaxIdentificationNumberApiResponse,
    UpdateMemberTaxIdentificationNumberApiRequest,
    UpdateMemberTaxIdentificationNumberApiResponse,
    GetMessagingConfigurationApiResponse,
    GetMemberShouldCollectTaxIdentificationNumberApiResponse,
    GetMagicalSyiEnabledApiResponse,
    GetMagicalSyiEnabledApiRequest,
    GetMemberGeoDataApiResponse
} from './types/webapi-member-generated';
import { MessagingEmailNotification } from './types/webapi-member-generated';
import { webApi } from './webapi-api';
import { isServer } from 'tradera/utils/nextjs';

export const memberApi = webApi.injectEndpoints({
    endpoints: (builder) => ({
        getPendingKycActions: builder.query<
            GetKycPendingActionsApiResponse,
            void
        >({
            query: () => {
                return {
                    url: '/api/webapi/member/kyc/pending-actions',
                    method: 'GET'
                };
            }
        }),
        getQuestionnaire: builder.query<
            GetKycQuestionnairePageDefinitionApiResponse,
            QuestionnaireRequest
        >({
            query: (params) =>
                `/api/webapi/member/kyc/questionnaire?${qs.stringify(params)}`
        }),
        initializeQuestionnaire: builder.mutation<
            InitializeQuestionnaireApiResponse,
            InitializeQuestionnaireApiRequest
        >({
            query: (params) => {
                return {
                    url: '/api/webapi/member/kyc/initialize-questionnaire',
                    method: 'POST',
                    body: params
                };
            }
        }),
        completeQuestionnaire: builder.mutation<
            CompleteQuestionnaireApiResponse,
            CompleteQuestionnaireApiRequest
        >({
            query: (params) => {
                return {
                    url: '/api/webapi/member/kyc/complete-questionnaire',
                    method: 'POST',
                    body: params
                };
            }
        }),
        getVerificationStatus: builder.query<
            GetMemberVerificationStatusApiResponse,
            void
        >({
            query: () => '/api/webapi/member/verification-status'
        }),
        setMakeAnOfferEnabled: builder.mutation<
            SetMakeAnOfferMemberEnabledApiResponse,
            SetMakeAnOfferMemberEnabledApiRequest
        >({
            query: (params) => {
                return {
                    url: '/api/webapi/member/make-an-offer-enabled',
                    method: 'PUT',
                    body: params
                };
            }
        }),
        getSilentMessagingEnabled: builder.query<
            GetMessagingSilentApiResponse,
            void
        >({
            query: () => '/api/webapi/member/messaging/silent'
        }),
        setSilentMessagingEnabled: builder.mutation<
            SetMessagingSilentApiResponse,
            SetMessagingSilentApiRequest
        >({
            query: (params) => {
                return {
                    url: '/api/webapi/member/messaging/silent',
                    method: 'PUT',
                    body: params
                };
            },
            onQueryStarted(request, { dispatch }) {
                dispatch(
                    memberApi.util.updateQueryData(
                        'getMemberEmailNotificationsSettingsViewModel',
                        undefined,
                        (draft) => {
                            Object.assign(draft, {
                                silentMessaging: {
                                    ...draft.silentMessaging,
                                    enabled: request.enabled
                                }
                            });
                        }
                    )
                );
            },
            invalidatesTags: ['memberemailnotificationssettingsviewmodel']
        }),
        getCompanyMoneyTransferLicenseStatus: builder.query<
            GetMemberCompanyMoneyTransferLicenseStatusApiResponse,
            void
        >({
            query: () =>
                '/api/webapi/member/company-money-transfer-license-status'
        }),
        getMemberEmailNotificationsSettingsViewModel: builder.query<
            GetMemberEmailNotificationsSettingsViewModelApiResponse,
            void
        >({
            query: () =>
                '/api/webapi/member/memberemailnotificationssettingsviewmodel',
            providesTags: ['memberemailnotificationssettingsviewmodel']
        }),
        setMessagingEmailNotify: builder.mutation<
            SetMessagingEmailNotifyApiResponse,
            SetMessagingEmailNotifyApiRequest
        >({
            query: (params) => {
                return {
                    url: '/api/webapi/member/messaging/emailnotify',
                    method: 'PUT',
                    body: params
                };
            },
            onQueryStarted(request, { dispatch }) {
                dispatch(
                    memberApi.util.updateQueryData(
                        'getMemberEmailNotificationsSettingsViewModel',
                        undefined,
                        (draft) => {
                            Object.assign(draft, {
                                messaging: {
                                    ...draft.messaging,
                                    emailNotifyAll:
                                        request.emailNotify ===
                                        MessagingEmailNotification.All
                                }
                            });
                        }
                    )
                );
            },
            invalidatesTags: ['memberemailnotificationssettingsviewmodel']
        }),
        getMemberHasTaxIdentificationNumber: builder.query<
            GetMemberHasTaxIdentificationNumberApiResponse,
            void
        >({
            query: () => '/api/webapi/member/has-tin'
        }),
        getMemberShouldCollectTaxIdentificationNumber: builder.query<
            GetMemberShouldCollectTaxIdentificationNumberApiResponse,
            void
        >({
            query: () => '/api/webapi/member/should-collect-tin'
        }),
        setMemberTaxIdentificationNumber: builder.mutation<
            UpdateMemberTaxIdentificationNumberApiResponse,
            UpdateMemberTaxIdentificationNumberApiRequest
        >({
            query: (params) => {
                return {
                    url: '/api/webapi/member/tin',
                    method: 'PUT',
                    body: params
                };
            }
        }),
        updateMemberEmailSettings: builder.mutation<
            UpdateMemberEmailSettingsApiResponse,
            UpdateMemberEmailSettingsApiRequest
        >({
            query: (params) => {
                return {
                    url: '/api/webapi/member/email-settings',
                    method: 'PUT',
                    body: params
                };
            }
        }),
        getCompanyDac7Status: builder.query<
            GetMemberCompanyDac7StatusApiResponse,
            void
        >({
            query: () => '/api/webapi/member/company-dac7-status'
        }),
        setMemberVatNumber: builder.mutation<
            UpdateMemberVatNumberApiResponse,
            UpdateMemberVatNumberApiRequest
        >({
            query: (params) => {
                return {
                    url: '/api/webapi/member/vat-number',
                    method: 'PUT',
                    body: params
                };
            }
        }),
        setMagicalSyi: builder.mutation<
            GetMagicalSyiEnabledApiResponse,
            GetMagicalSyiEnabledApiRequest
        >({
            query: (params) => {
                return {
                    url: '/api/webapi/member/seller-magical-syi-enabled',
                    method: 'PUT',
                    body: params
                };
            }
        }),
        getMagicalSyiEnabled: builder.query<
            GetMagicalSyiEnabledApiResponse,
            void
        >({
            query: () => '/api/webapi/member/seller-magical-syi-enabled'
        }),
        getMessagingConfiguration: builder.query<
            GetMessagingConfigurationApiResponse,
            void
        >({
            query: () => '/api/webapi/member/messaging/messaging-configuration'
        }),
        getMemberGeoData: builder.query<GetMemberGeoDataApiResponse, void>({
            query: () => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/member/geo`;
            }
        })
    })
});

export const {
    useGetPendingKycActionsQuery,
    useGetQuestionnaireQuery,
    useInitializeQuestionnaireMutation,
    useCompleteQuestionnaireMutation,
    useGetVerificationStatusQuery,
    useSetMakeAnOfferEnabledMutation,
    useGetSilentMessagingEnabledQuery,
    useSetSilentMessagingEnabledMutation,
    useGetCompanyMoneyTransferLicenseStatusQuery,
    useGetMemberEmailNotificationsSettingsViewModelQuery,
    useSetMessagingEmailNotifyMutation,
    useUpdateMemberEmailSettingsMutation,
    useGetCompanyDac7StatusQuery,
    useSetMemberVatNumberMutation,
    useGetMemberHasTaxIdentificationNumberQuery,
    useGetMemberShouldCollectTaxIdentificationNumberQuery,
    useSetMemberTaxIdentificationNumberMutation,
    useSetMagicalSyiMutation,
    useGetMagicalSyiEnabledQuery,
    useGetMessagingConfigurationQuery,
    useGetMemberGeoDataQuery,
    endpoints
} = memberApi;
